<template>
    <div>
        <!-- 삽입 버튼 (편집 모드만) -->
        <div v-if="!readonly" class="mb-2">
            <b-button size="sm" variant="outline-primary" @click="openInsertModal">
                스니펫 삽입
            </b-button>
        </div>

        <b-row>
            <b-col cols="6">
                <label class="font-weight-bold mb-1">편집</label>
                <b-form-textarea
                    ref="textarea"
                    :value="value"
                    @input="onInput"
                    :readonly="readonly"
                    style="font-family: monospace; font-size: 0.85rem; min-height: 400px; resize: vertical;"
                    placeholder="템플릿 내용을 입력하세요..."
                />

                <!-- 참조된 스니펫 뱃지 -->
                <div v-if="snippetRefs.length" class="mt-2">
                    <div class="small text-muted mb-1 font-weight-bold">참조된 스니펫</div>
                    <b-badge
                        v-for="ref in snippetRefs"
                        :key="`${ref.code}/${ref.version}`"
                        variant="info"
                        class="mr-1 mb-1"
                        style="cursor: pointer;"
                        @click="openRefEditModal(ref)"
                    >
                        {{ ref.code }} v{{ ref.version }}
                    </b-badge>
                </div>
            </b-col>
            <b-col cols="6">
                <label class="font-weight-bold mb-1">미리보기</label>
                <SnippetPreview :content="previewContent" />
            </b-col>
        </b-row>

        <!-- 스니펫 삽입 모달 -->
        <b-modal
            :id="`snippet-insert-${_uid}`"
            title="스니펫 삽입"
            size="xl"
            hide-footer
        >
            <b-row class="mb-2">
                <b-col cols="8">
                    <b-input-group>
                        <b-form-input
                            v-model="insertSearch"
                            placeholder="코드/라벨 검색"
                            @keyup.enter="searchInsertSnippets"
                        />
                        <b-input-group-append>
                            <b-button variant="primary" @click="searchInsertSnippets">검색</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="5">
                    <b-table
                        :items="insertList"
                        :fields="insertFields"
                        small
                        hover
                        selectable
                        select-mode="single"
                        show-empty
                        :busy="insertLoading"
                        @row-selected="onInsertRowSelected"
                    >
                        <template #table-busy>
                            <div class="text-center py-3"><b-spinner /></div>
                        </template>
                        <template #empty>
                            <PageEmptyComponent
                                compact
                                text="조회된 스니펫이 없습니다."
                                :error="insertError"
                                @retry="loadInsertSnippets(insertPage)"
                            />
                        </template>
                    </b-table>
                    <b-pagination
                        v-if="insertTotal > insertPerPage"
                        v-model="insertPage"
                        :total-rows="insertTotal"
                        :per-page="insertPerPage"
                        size="sm"
                        @change="loadInsertSnippets"
                    />
                </b-col>
                <b-col cols="7">
                    <div class="border rounded p-2" style="min-height: 300px;">
                        <label class="font-weight-bold small">미리보기</label>
                        <div v-if="!insertSelected" class="text-muted text-center py-5">
                            <small>스니펫을 선택하면 미리보기가 표시됩니다.</small>
                        </div>
                        <div v-else-if="insertPreviewFailed" class="text-danger text-center py-5">
                            <small>미리보기를 불러오지 못했습니다. 다시 선택해 주세요.</small>
                        </div>
                        <SnippetPreview v-else :content="insertPreviewContent" />
                    </div>
                    <div v-if="insertSelected" class="mt-2 d-flex justify-content-end">
                        <b-button variant="primary" @click="confirmInsert">삽입</b-button>
                    </div>
                </b-col>
            </b-row>
        </b-modal>

        <!-- 참조 스니펫 편집 모달 -->
        <b-modal
            :id="`snippet-ref-edit-${_uid}`"
            :title="refEditSnippet ? `스니펫 편집: ${refEditSnippet.code} v${refEditSnippet.version}` : '스니펫 편집'"
            size="xl"
            hide-footer
            @hidden="resetRefEdit"
        >
            <div v-if="refEditLoading" class="text-center py-5">
                <b-spinner variant="primary" />
            </div>
            <div v-else-if="refEditSnippet">
                <b-row class="mb-2">
                    <b-col cols="4">
                        <b-form-group label="코드">
                            <b-form-input :value="refEditSnippet.code" readonly />
                        </b-form-group>
                    </b-col>
                    <b-col cols="2">
                        <b-form-group label="버전">
                            <b-form-input :value="refEditSnippet.version" readonly />
                        </b-form-group>
                    </b-col>
                    <b-col cols="6">
                        <b-form-group label="라벨">
                            <b-form-input v-model="refEditForm.label" :readonly="!refEditMode" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-form-group label="내용">
                    <b-form-textarea
                        v-model="refEditForm.content"
                        :readonly="!refEditMode"
                        style="font-family: monospace; font-size: 0.85rem; min-height: 300px;"
                    />
                </b-form-group>
                <div class="d-flex justify-content-end mt-2">
                    <template v-if="!refEditMode">
                        <b-button variant="primary" @click="refEditMode = true">수정</b-button>
                    </template>
                    <template v-else>
                        <b-button
                            variant="primary"
                            :disabled="refEditSaving"
                            class="mr-2"
                            @click="saveRefEdit"
                        >
                            저장
                        </b-button>
                        <b-button variant="secondary" :disabled="refEditSaving" @click="refEditMode = false">
                            취소
                        </b-button>
                    </template>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import SnippetPreview from './SnippetPreview.vue';
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';

const SNIPPET_REF_RE = /\{%\s*snippet\s+(\S+)\s+(\d+)\s*%\}/g;

export default {
    name: 'SnippetEditor',
    components: { SnippetPreview, PageEmptyComponent },
    props: {
        value: {
            type: String,
            default: ''
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            previewContent: this.value || '',
            debounceTimer: null,
            // 직접 입력해 보낸 값. value 가 이 값으로 돌아오면 디바운스에 맡기고, 다를 때(버전 전환·삽입)만 바로 미리보기
            lastEmitted: null,

            // 삽입 모달
            insertSearch: '',
            insertList: [],
            insertLoading: false,
            insertError: '',
            insertListSeq: 0,
            insertPage: 1,
            insertPerPage: 10,
            insertTotal: 0,
            insertSelected: null,
            insertPreviewContent: '',
            insertPreviewFailed: false,
            insertPreviewSeq: 0,
            insertFields: [
                { key: 'code', label: '코드' },
                { key: 'version', label: 'v', thStyle: { width: '50px' } },
                { key: 'label', label: '라벨' },
            ],

            // 참조 스니펫 편집 모달
            refEditSnippet: null,
            refEditForm: { label: '', content: '' },
            refEditMode: false,
            refEditLoading: false,
            refEditSaving: false,
        };
    },
    computed: {
        snippetRefs() {
            if (!this.value) return [];
            const refs = [];
            const seen = new Set();
            let m;
            const re = new RegExp(SNIPPET_REF_RE.source, 'g');
            while ((m = re.exec(this.value)) !== null) {
                const key = `${m[1]}/${m[2]}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    refs.push({ code: m[1], version: Number(m[2]) });
                }
            }
            return refs;
        }
    },
    watch: {
        value(newVal) {
            const echo = newVal === this.lastEmitted;
            this.lastEmitted = null;
            if (echo) return;
            clearTimeout(this.debounceTimer);
            this.previewContent = newVal;
        }
    },
    methods: {
        onInput(val) {
            this.lastEmitted = val;
            this.$emit('input', val);
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.previewContent = val;
            }, 300);
        },

        // ── 삽입 모달 ──────────────────────────────────────────
        openInsertModal() {
            this.insertSearch = '';
            this.insertSelected = null;
            this.insertPreviewContent = '';
            this.loadInsertSnippets(1);
            this.$bvModal.show(`snippet-insert-${this._uid}`);
        },
        async loadInsertSnippets(page = 1) {
            const seq = ++this.insertListSeq;
            this.insertLoading = true;
            this.insertError = '';
            try {
                const resp = await this.$axios.get('/mail/snippet', {
                    params: { keyword: this.insertSearch, page, size: this.insertPerPage }
                });
                if (seq !== this.insertListSeq) return;
                this.insertList = resp.data.results || resp.data.items || resp.data;
                this.insertTotal = resp.data.total || this.insertList.length;
                this.insertPage = page;
            } catch (e) {
                if (seq !== this.insertListSeq) return;
                this.insertList = [];
                // 실패를 '스니펫 없음'으로 보이지 않게 오류 + 다시 시도 (401 은 인터셉터가 로그인으로 보낸다)
                if (e.response?.status !== 401) this.insertError = getErrorMessage(e, '스니펫 목록을 불러오지 못했습니다.');
            } finally {
                if (seq === this.insertListSeq) this.insertLoading = false;
            }
        },
        searchInsertSnippets() { this.loadInsertSnippets(1); },
        async onInsertRowSelected(rows) {
            const seq = ++this.insertPreviewSeq;
            this.insertPreviewFailed = false;
            if (!rows.length) { this.insertSelected = null; return; }
            const s = rows[0];
            this.insertSelected = s;
            try {
                const resp = await this.$axios.get(`/mail/snippet/${s.code}/${s.version}`);
                if (seq !== this.insertPreviewSeq) return;
                this.insertPreviewContent = resp.data.content || '';
            } catch (e) {
                // 목록 응답엔 content 가 없어 빈 미리보기가 되므로 실패를 그대로 알린다
                if (seq === this.insertPreviewSeq) this.insertPreviewFailed = true;
            }
        },
        confirmInsert() {
            if (!this.insertSelected) return;
            const { code, version } = this.insertSelected;
            const syntax = `{{% snippet ${code} ${version} %}}`;
            this.insertAtCursor(syntax);
            this.$bvModal.hide(`snippet-insert-${this._uid}`);
        },
        insertAtCursor(text) {
            const el = this.$refs.textarea?.$el?.querySelector('textarea') || this.$refs.textarea?.$el;
            if (el && el.selectionStart !== undefined) {
                const start = el.selectionStart;
                const end = el.selectionEnd;
                const current = this.value || '';
                const newVal = current.slice(0, start) + text + current.slice(end);
                this.$emit('input', newVal);
                this.$nextTick(() => {
                    el.selectionStart = el.selectionEnd = start + text.length;
                    el.focus();
                });
            } else {
                this.$emit('input', (this.value || '') + text);
            }
        },

        // ── 참조 스니펫 편집 모달 ─────────────────────────────
        async openRefEditModal(ref) {
            this.refEditSnippet = null;
            this.refEditMode = false;
            this.refEditLoading = true;
            this.$bvModal.show(`snippet-ref-edit-${this._uid}`);
            try {
                const resp = await this.$axios.get(`/mail/snippet/${ref.code}/${ref.version}`);
                this.refEditSnippet = resp.data;
                this.refEditForm = {
                    label: resp.data.label || '',
                    content: resp.data.content || '',
                };
            } catch (e) {
                this.$bvToast.toast('스니펫 정보를 불러오는데 실패했습니다.', { variant: 'danger', title: '오류' });
                this.$bvModal.hide(`snippet-ref-edit-${this._uid}`);
            } finally {
                this.refEditLoading = false;
            }
        },
        async saveRefEdit() {
            // 참조 스니펫은 쓰는 템플릿·상위 스니펫에 바로 반영되므로 사용처가 있으면 확인
            const d = this.refEditSnippet;
            const templates = new Set((d.usage_templates || []).map(t => t.template_id)).size;
            const parents = d.usage_parent_snippet_count || 0;
            if (templates || parents) {
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '스니펫 저장',
                    message: `${d.code} v${d.version} 을 직접 쓰는 템플릿 ${templates}개, 이 스니펫을 포함한 상위 스니펫 ${parents}개에 바로 반영됩니다(상위 스니펫을 쓰는 템플릿에도 반영). 그래도 저장할까요?`,
                });
                if (!ok) return;
            }
            this.refEditSaving = true;
            try {
                await this.$axios.patch(
                    `/mail/snippet/${this.refEditSnippet.code}/${this.refEditSnippet.version}`,
                    { label: this.refEditForm.label, content: this.refEditForm.content }
                );
                this.$bvToast.toast('저장되었습니다.', { variant: 'success', title: '성공' });
                this.refEditMode = false;
            } catch (e) {
                this.$bvToast.toast('저장에 실패했습니다.', { variant: 'danger', title: '오류' });
            } finally {
                this.refEditSaving = false;
            }
        },
        resetRefEdit() {
            this.refEditSnippet = null;
            this.refEditForm = { label: '', content: '' };
            this.refEditMode = false;
        },
    },
    beforeDestroy() {
        clearTimeout(this.debounceTimer);
    }
};
</script>
